import { useEffect, useMemo, useState } from 'react';

import { useCheckout } from '@/hooks/useCheckout';
import { useGetPaymentGateways } from '@/hooks/useGetPaymentGateways';
import {
  EXPRESS_PAYMENT_PREORDER_COUTRY_LIMITATION,
  PAYMENT_APPS_ID,
} from '@/lib/const';
import { PAYMENT_TYPES } from '@/lib/payment/types';
import { useRegionProvider } from '@/providers/RegionProvider';

export const useApplePayConfiguration = () => {
  const { country } = useRegionProvider();
  const {
    snapshot: {
      checkout: { id, totalPrice },
      orderId,
      preOrderData,
    },
  } = useCheckout<true>();
  const [isApplePayExpressActivated, setIsApplePayExpressActivated] =
    useState(false); // Whether Apple Pay express payment method is activated on the Adyen merchant account
  const isDepositTransaction = !!preOrderData?.deposit;
  const transactionAmount = useMemo((): number => {
    let amount;
    if (!isDepositTransaction) {
      amount = totalPrice?.gross.amount;
    } else {
      amount = preOrderData?.deposit;
    }

    return amount ?? 0;
  }, [isDepositTransaction, totalPrice, preOrderData]);

  const { gatewayConfigs, isLoading: isGatewayConfigLoading } =
    useGetPaymentGateways(orderId || id, transactionAmount);

  const gatewayId = useMemo(() => {
    const applePayGateway = gatewayConfigs.find((g) =>
      g.paymentMethods.some((m) => m.type === PAYMENT_TYPES.APPLE_PAY_EXPRESS),
    );

    return applePayGateway?.id;
  }, [gatewayConfigs]);

  const isExpressPaymentAllowed = useMemo(() => {
    if (!isDepositTransaction) return true;
    return (
      EXPRESS_PAYMENT_PREORDER_COUTRY_LIMITATION.length === 0 ||
      EXPRESS_PAYMENT_PREORDER_COUTRY_LIMITATION.includes(country.code)
    );
  }, [isDepositTransaction, country.code]);

  useEffect(() => {
    const isActivated = PAYMENT_APPS_ID.some((appId) => {
      const gateway = gatewayConfigs.find((item) => item.id === appId);
      if (gateway) {
        return gateway?.paymentMethods.some(
          (method) => method.type === PAYMENT_TYPES.APPLE_PAY_EXPRESS,
        );
      }
      return false;
    });

    if (isActivated) {
      setIsApplePayExpressActivated(true);
    }
  }, [gatewayConfigs]);

  return {
    isActivated: isApplePayExpressActivated,
    isExpressPaymentAllowed,
    isGatewayConfigLoading,
    gatewayId,
    gatewayConfigs,
  };
};
